import React, { FC, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { Dialog } from "@natera/material/lib/dialog";
import { Button } from "@natera/form";
import { routes } from "@app/routing";
import { FetalSexContext } from "@app/provider";
import { LoadingContext } from "@natera/platform/lib/components/context";
import { HEAP_EVENTS } from "@app/provider/types";

import "./fetalSexDialog.scss";
import { useRemoveAttribute } from "@app/hooks";

const messages = defineMessages({
  fetalSexDialogTitle: {
    id: "fetalSexDialogTitle",
    defaultMessage: "Would you like to learn the fetal sex?",
  },
  fetalSexDialogDesc: {
    id: "fetalSexDialogDesc",
    defaultMessage:
      "Please select whether you would like to include the fetal sex of your baby in your results.",
  },
  fetalSexDialogAccept: {
    id: "fetalSexDialogAccept",
    defaultMessage: "Yes, include fetal sex",
  },
  fetalSexDialogReject: {
    id: "fetalSexDialogReject",
    defaultMessage: "No, don’t include fetal sex",
  },
});

export interface FetalSexModalProps {
  onCloseDialog: () => void;
  orderUidPublic: string;
  testUid: string;
  testDocumentUid: string;
  limsClinicId?: number;
}
const FetalSexModal: FC<FetalSexModalProps> = ({
  onCloseDialog,
  orderUidPublic,
  testUid,
  testDocumentUid,
  limsClinicId,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { handleUpdateFetalSexIsShown, updateFetalSexIsShownIsLoading } =
    useContext(FetalSexContext);

  useRemoveAttribute([
    {
      selector: 'div[role="alertdialog"]',
      attribute: "role",
      root: document.body,
    },
    {
      selector: ".mdc-dialog__surface",
      attribute: "aria-modal",
    },
  ]);

  const handleFetalSexDialogReject = async () => {
    heap.track(HEAP_EVENTS.upp_fetalsex_hide_on_PDF, {
      lims_clinic_id: limsClinicId,
      public_order_uid: orderUidPublic,
    });
    const res = await handleUpdateFetalSexIsShown(
      orderUidPublic,
      testUid,
      false
    );

    if (!res?.documentUid) {
      onCloseDialog();
      return;
    }
    onCloseDialog();
    navigate(
      routes.testResultPdfPage(orderUidPublic, testUid, res.documentUid)
    );
  };

  const handleFetalSexDialogAccept = async () => {
    heap.track(HEAP_EVENTS.upp_fetalsex_view_on_PDF, {
      lims_clinic_id: limsClinicId,
      public_order_uid: orderUidPublic,
    });
    await handleUpdateFetalSexIsShown(orderUidPublic, testUid, true);
    onCloseDialog();
    navigate(
      routes.testResultPdfPage(orderUidPublic, testUid, testDocumentUid)
    );
  };

  return (
    <Dialog
      className="fetal-sex-modal"
      data-testid="fetal-sex-modal"
      title={intl.formatMessage(messages.fetalSexDialogTitle)}
      onClose={onCloseDialog}
      showCrossButton
      role={"dialog"}
      aria-modal={true}
      actions={
        <>
          <LoadingContext isLoading={updateFetalSexIsShownIsLoading}>
            <Button
              onClick={handleFetalSexDialogReject}
              outlined
              data-testid="fetalSexDialogReject"
            >
              {intl.formatMessage(messages.fetalSexDialogReject)}
            </Button>
            <Button
              onClick={handleFetalSexDialogAccept}
              unelevated
              data-testid="fetalSexDialogAccept"
            >
              {intl.formatMessage(messages.fetalSexDialogAccept)}
            </Button>
          </LoadingContext>
        </>
      }
    >
      <div className="fetal-sex-modal__container">
        <p>{intl.formatMessage(messages.fetalSexDialogDesc)}</p>
      </div>
    </Dialog>
  );
};

export default FetalSexModal;
